import React, { useEffect, useRef, useState } from 'react';
import Like from '../../../../../Assets/Lens/Like.svg';
import LikeGreen from '../../../../../Assets/Lens/LikeGreen.svg';
import { formatBadge, formatDescription, formatNumber, formatTimeDifference, getCachedMediaUrl, getInitials } from '../../../../../lib/utils/utils';
import { useDispatch, useSelector } from 'react-redux';
import { checkLikeOnComment, getChildComments, getPostProfile, likeComment, postChildComment, unlikeComment } from '../../../../../lib/utils/apiCalls';
import ChildComment from './ChildComment';
import { useOutsideClickHandler } from '../../../../../Hooks/useOutsideClick';
import VerticalDot from '../../../../../Assets/VerticalDot.svg';
import CommentKebab from './CommentKebab';
import { closeModal, setSelectedComment, toggleChildCommentDeleted } from '../../../../../Features/lensSlice';
import { useOutsideClickForTwoRef } from '../../../../../Hooks/useOutsideClickForTwoRef';
import { showToast } from '../../../../../Components/Toaster/Toaster';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

function Comment({ comment, mention, postId, postCommentAllow }) {
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams();
    
    const wrapperRef = useRef(null);
    const inputRef = useRef(null);

    useOutsideClickHandler(wrapperRef, () => {
        setReplyOpen(false);
        setCommentID('');
    });

    const childCommentDeleted = useSelector((state) => state.lens.childCommentDeleted);

    const commentActionButtonRef = useRef(null);
    const commentKebabRef = useRef(null);

    useOutsideClickForTwoRef(commentActionButtonRef, commentKebabRef, () => {
        setCommentKebabOpen(false);
        dispatch(setSelectedComment({}));
    });

    const [commentKebabOpen, setCommentKebabOpen] = useState(false);

    const [inputValue, setInputValue] = useState('');

    const [childcomments, setChildComments] = useState([]);
    const [userProfile, setUserProfile] = useState({});
    const [myProfile, setMyProfile] = useState({});

    const [replyOpen, setReplyOpen] = useState(false);
    const [commentID, setCommentID] = useState('');
    const [commentLikesCount, setCommentLikesCount] = useState(0);
    const [likedByUser, setLikedByUser] = useState(false);

    const storeData = useSelector((state) => state.lens.storeData.storeData);
    const selectedComment = useSelector((state) => state.lens.modal.selectedComment);

    const handleChange = (e) => {
        setInputValue(e.target.value);
    }

    const handleOpenReplyBox = () => {
        if(postCommentAllow) {
            setReplyOpen(true);
            setCommentID(comment.id);
            setTimeout(() => {
                inputRef.current.focus();
            }, 0);
        }
    }

    const handleCommentLikeClick = async () => {
        if(likedByUser) {
            try {
                const response = await unlikeComment(comment.id);
                setCommentLikesCount(response.noOfLikes);
            }
            catch(err) {
                console.error(err);
            }
        }
        else {
            try {
                const response = await likeComment(comment.id);
                setCommentLikesCount(response.noOfLikes);
            }
            catch(err) {
                console.error(err);
            }
        }
        try {
            const response = await checkLikeOnComment(comment.id);
            setLikedByUser(response);
        }
        catch(err) {
            console.error(err);
        }
    }

    const handleCommentReply = async (e) => {
        if(e.key === 'Enter') {
            const callData = {
                postId: postId,
                comment: inputValue,
                ParentId: comment.id,
            }

            try {
                const response = await postChildComment(callData);
                if(response?.status === 200) {
                    setInputValue('');
                    setChildComments([ ...childcomments, response.data.data ]);
                    setReplyOpen(false);
                    setCommentID('');
                }
            }
            catch(err) {
                showToast(`${err}`, 'error');
            }
        }
    }

    useEffect(() => {
        const getChildCommentsFromAPI = async () => {
            const callData = {
                postID: postId,
                commentID: comment.id,
            }
            const response = await getChildComments(callData);
            setChildComments(response);
        }

        getChildCommentsFromAPI();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setCommentLikesCount(comment.noOflikes);
        const userId = comment.UserId || comment.SellerUserId;

        const getUserProfile = async () => {
            const profile = await getPostProfile(userId);
            setUserProfile(profile);
        }

        const checkUserLikeOnComment = async () => {
            const response = await checkLikeOnComment(comment.id);
            setLikedByUser(response);
        }

        Object.keys(comment).length > 0 && getUserProfile();
        Object.keys(comment).length > 0 && checkUserLikeOnComment();
    }, [comment]);

    useEffect(() => {
        const getUserProfile = async () => {
            const profile = await getPostProfile(storeData.id);
            setMyProfile(profile);
        }

        Object.keys(storeData).length > 0 && getUserProfile();
    }, [storeData]);

    const handleCommentActionButtonClick = () => {
        if(selectedComment.id === comment.id) {
            setCommentKebabOpen(false);
            dispatch(setSelectedComment({}));
        }
        else {
            dispatch(setSelectedComment(comment));
            setCommentKebabOpen(true);
        }
    }

    const handleCommentUserClick = () => {
        const userId = comment.UserId || comment.SellerUserId;

        if(params.lensId !== userId) {
            navigate(`${location.pathname.split('/').slice(0, -1).join('/')}/${userId}`);
        }
        dispatch(closeModal());
    }

    useEffect(() => {
        async function handleGetNewChildComments() {
            const callData = {
                postID: postId,
                commentID: comment.id,
            }

            const response = await getChildComments(callData);
            setChildComments(response);
        }

        if(childCommentDeleted) {
            if(selectedComment.ParentId === comment.id) {
                handleGetNewChildComments();
            }
            dispatch(toggleChildCommentDeleted());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [childCommentDeleted])

    return (
        <>
            <div className='flex gap-[5px]'>
                <div className="w-8 h-8 rounded-full overflow-hidden">
                    {
                        userProfile?.ProfilePic ? 
                        <img className='w-full h-full object-cover' src={getCachedMediaUrl(userProfile.ProfilePic)} alt="" /> : 
                        <div className='w-full h-full bg-[#EAEAEA] flex items-center justify-center'>
                            <p className='font-mulish font-bold text-[14px] leading-4 text-[#666666]'>{comment.UserId ? getInitials(userProfile?.fullName) : getInitials(userProfile?.name)}</p>
                        </div>
                    }
                </div>

                <div className="flex flex-col gap-1 w-[calc(100%-32px)]">
                    <div className="flex items-center justify-between">
                        <div className="flex items-baseline gap-1 font-semibold select-none">
                            <p onClick={handleCommentUserClick} className='text-[14px] leading-[26px] hover:underline cursor-pointer'>{comment.UserId ? userProfile?.fullName : userProfile?.name}</p>

                            <span className='text-[10px] leading-3 text-[#808080]'>
                                {
                                    comment.UserId ? 
                                    (userProfile.milestone ? `${userProfile.milestone === 'No Review' ? userProfile.milestone : `${formatBadge(userProfile.milestone)}`}` : '') : 
                                    `${userProfile.favourite ? formatNumber(userProfile.favourite) : '0'} Shoppers' Favorite`
                                }
                            </span>
                        </div>

                        <div className="flex items-center gap-3">
                            <div className="flex items-center gap-[10px]">
                                <div className="rounded-full w-1 h-1 bg-[#757575]"></div>

                                <p className='font-normal text-[12px] leading-6 text-[#A0A0A0]'>{formatTimeDifference(comment?.createdAt)} ago</p>
                            </div>

                            <div onClick={handleCommentActionButtonClick} ref={commentActionButtonRef} className="relative size-6 flex items-center justify-center outline-none cursor-pointer">
                                <img className='select-none' src={VerticalDot} alt="" />

                                {
                                    selectedComment.id === comment.id && 
                                    <CommentKebab 
                                        kebabOpen={commentKebabOpen} 
                                        boxRef={commentKebabRef} 
                                        comment={comment} 
                                    />
                                }
                            </div>
                        </div>
                    </div>

                    <div className="w-[95%]">
                        <p className='w-full font-normal text-[12px] leading-6 text-[#121212]'>
                            {/* {mention && <span className='font-bold cursor-pointer text-primaryColor'>Saiful Ameen </span>} */}
                            <span dangerouslySetInnerHTML={{ __html: formatDescription(comment?.comment) }} />
                        </p>

                        <div className="flex items-center gap-[10px] py-1">
                            <div className="flex items-center">
                                <div className="w-[38px] h-[38px] flex items-center justify-center">
                                    <button onClick={handleCommentLikeClick}>
                                        <img src={likedByUser ? LikeGreen : Like} alt="" />
                                    </button>
                                </div>

                                <p className='font-normal text-[14px] leading-[26px] text-[#B0B0B0]'>{commentLikesCount}</p>
                            </div>

                            <div className="w-[1px] h-3 bg-[#757575]"></div>

                            {
                                !replyOpen && 
                                <div 
                                    className='cursor-text flex-grow outline-none font-medium text-[12px] leading-6' 
                                    onClick={handleOpenReplyBox}
                                >
                                    <p className='text-[#B0B0B0] select-none'>Reply</p>
                                </div>
                            }
                        </div>
                    </div>
                </div>
            </div>

            {
                (replyOpen && commentID === comment.id) &&
                <div ref={wrapperRef} className="mb-2 w-[calc(100%-32px)] ml-auto flex items-center justify-between gap-2">
                    <div className="w-8 h-8 rounded-full overflow-hidden">
                        {
                            myProfile?.ProfilePic ? 
                            <img className='w-full h-full object-cover' src={getCachedMediaUrl(myProfile.ProfilePic)} alt="" /> : 
                            <div className='w-full h-full bg-[#EAEAEA] flex items-center justify-center'>
                                <p className='font-mulish font-bold text-[14px] leading-4 text-[#666666]'>{getInitials(myProfile.name)}</p>
                            </div>
                        }
                    </div>

                    <input 
                        ref={inputRef} 
                        className='w-[calc(100%-40px)] border-2 border-[#EAEAEA] px-[6px] h-full rounded-full flex-grow outline-none font-medium text-[12px] leading-6 placeholder:text-[#B0B0B0]' 
                        type="text" 
                        placeholder='Reply' 
                        value={inputValue} 
                        onChange={handleChange} 
                        onKeyDown={handleCommentReply} 
                    />
                </div>
            }

            {
                [ ...childcomments ]
                .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
                .map(childComment => {
                    return (
                        <ChildComment 
                            key={childComment.id} 
                            comment={childComment} 
                        />
                    )
                })
            }
        </>
    )
}

export default Comment;
